/* eslint-disable camelcase */
import React from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {DatePicker} from 'rsuite';
import {t} from '@lingui/core/macro';

import {userAPI, IUserOutput} from 'entities/user';
import {tariffAPI, ITariff, getTariffOption} from 'entities/tariff';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Select from 'shared/ui/Select/Select';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import {getFormattedDate} from 'shared/lib/datetime';

import styles from './UserTariffForm.module.scss';

interface IUserTariffFormProps {
    onSuccess: () => void,
    item?: IUserOutput,
    onClose?: () => void,
}

interface IUserTariffForm {
    tariff: string,
    active_to?: Date | null
    endless: boolean,
}

const UserTariffForm: React.FC<IUserTariffFormProps> = ({item, onSuccess, onClose}) => {
    const [changeTariff, {isLoading: isLoadingChange}] = userAPI.useChangeUserTariffMutation();
    const {data: tariffs} = tariffAPI.useGetTariffsQuery({field: 'price', direction: 'asc'});

    const {register, handleSubmit, watch, formState, control} = useForm<IUserTariffForm>({mode: 'onChange'});
    const {errors} = formState;

    const onSubmit: SubmitHandler<IUserTariffForm> = async({
        tariff,
        endless,
        active_to = null,
    }) => {
        try {
            await changeTariff({
                id: item?.id ?? 0,
                tariff: tariff,
                active_to: endless ? null : getFormattedDate(active_to, {format: 'YYYY-MM-DD'}),
            }).unwrap();

            onSuccess();
        } catch (e) {
        }
    };

    const isEndlessChecked = watch('endless');

    const getTariffOptionWithArchive = (tariff: ITariff) => {
        const option = getTariffOption(tariff);
        return {
            ...option,
            label: tariff.is_archive ? `${option.label} (арх.)` : option.label,
        };
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    error={errors?.tariff?.message}
                    label="Тариф"
                >
                    {
                        tariffs
                        && <Select
                            rules={{required: 'Выберите тариф'}}
                            control={control}
                            name="tariff"
                            data={tariffs.map(getTariffOptionWithArchive)}
                            placeholder="Выберите тариф"
                        />
                    }
                </FormGroupWithError>

                {
                    !isEndlessChecked
                    && <FormGroupWithError
                        error={errors?.active_to?.message}
                        label="Активен до"
                    >
                        <Controller
                            control={control}
                            name="active_to"
                            rules={{required: 'Выберите дату активности тарифа'}}
                            render={({field: {onChange, value}}) => (
                                <DatePicker
                                    value={value}
                                    onChange={onChange}
                                    format="dd.MM.yyyy"
                                    placeholder="ДД.ММ.ГГГГ"
                                    oneTap
                                    appearance="subtle"
                                />
                            )}
                        />
                    </FormGroupWithError>
                }
                <div className={styles.text}>
                    <Checkbox
                        textStyle={styles.checkbox}
                        {...register('endless')}
                    >
                        Бессрочно?
                    </Checkbox>
                </div>
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={t`Назначить`}
                primaryButtonType="submit"
                primaryButtonLoading={isLoadingChange}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default UserTariffForm;